import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowLeft, ShieldCheck, Eye, Radar, FileCheck2, Check, X, User, GraduationCap, Globe2, Wallet, Clock, Star } from "lucide-react";
import Marquee from "@/components/Marquee";
import StatsBand from "@/components/StatsBand";
import GoldSeal from "@/components/GoldSeal";
import SectionHeading from "@/components/SectionHeading";
import TrackingWidget from "@/components/TrackingWidget";
import Testimonials from "@/components/Testimonials";
import FaqAccordion from "@/components/FaqAccordion";
import ConsultationForm from "@/components/ConsultationForm";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { fetchServices, fetchLawyers, fetchTestimonials, fetchPosts, fetchFaqs } from "@/lib/api";
import { MARQUEE_ITEMS } from "@/data/site";
import { toFa } from "@/lib/fa";

const HeroLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="noise-overlay relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0" data-testid="hero-parallax-image">
        <img src="https://images.unsplash.com/photo-1775144657566-e5b093073baf?auto=format&fit=crop&w=1920&q=80" alt="" className="img-treatment h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-l from-navy-950 via-navy-950/80 to-navy-950/50" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 15%, rgba(212,175,55,0.14) 0%, rgba(7,10,18,0) 65%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-24 pt-36 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
        <div>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="mb-7 inline-flex items-center gap-3 rounded-full border border-gold/30 bg-gold/5 px-5 py-2">
            <span className="h-2 w-2 animate-pulse-dot rounded-full bg-gold" />
            <span className="font-display text-xs font-bold text-gold sm:text-sm">گروه وکلای مهاجرت ۷ به مدیریت دکتر محمد احمدی</span>
          </motion.div>

          <h1 className="text-4xl font-black leading-[1.35] tracking-tightest sm:text-5xl lg:text-6xl lg:leading-[1.3]" data-testid="hero-headline">
            <HeroLine delay={0.25}>آینده شما در</HeroLine>
            <HeroLine delay={0.4}><span className="gold-text">سرزمینِ فرصت‌ها</span></HeroLine>
            <HeroLine delay={0.55}>از امروز شروع می‌شود</HeroLine>
          </h1>

          <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }} className="mt-7 max-w-xl text-base leading-8 text-slate-300 sm:text-lg">
            وکالت تخصصی مهاجرت به <span className="font-bold text-gold">آمریکا، کانادا، استرالیا و حوزه شنگن</span> — با وکلای رسمی، قرارداد شفاف و رهگیری آنلاین پرونده؛ از ارزیابی اولیه تا لحظه پرواز.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.0 }} className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-gold" data-testid="hero-cta-consult-button">درخواست مشاوره رایگان<ArrowLeft size={17} /></Link>
            <Link to="/tracking" className="btn-ghost" data-testid="hero-cta-tracking-button">رهگیری پرونده</Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400 sm:text-sm">
            <span className="flex items-center gap-2"><Check size={14} className="text-gold" />پناهندگی و بشر دوستانه در ۵۰ تا ۶۰ روز کاری</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-gold" />تسویه‌حساب پس از رسیدن به مقصد</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-gold" />بدون نیاز به مدرک زبان و تمکن مالی</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.35 }} className="mt-9 flex flex-wrap items-center gap-3" data-testid="hero-flags-row">
            {[
              { flag: "https://flagcdn.com/w160/us.png", label: "آمریکا", to: "/countries/usa" },
              { flag: "https://flagcdn.com/w160/ca.png", label: "کانادا", to: "/countries/canada" },
              { flag: "https://flagcdn.com/w160/au.png", label: "استرالیا", to: "/countries/australia" },
              { flag: "https://flagcdn.com/w160/eu.png", label: "اروپا — شنگن", to: "/countries/schengen" },
            ].map((f) => (
              <Link key={f.to} to={f.to} className="flex items-center gap-2.5 rounded-full border border-gold/20 bg-navy-900/70 py-1.5 pe-5 ps-1.5 backdrop-blur transition-all duration-300 hover:border-gold/60 hover:bg-navy-850" data-testid={`hero-flag-${f.to.split('/').pop()}`}>
                <img src={f.flag} alt={f.label} className="h-7 w-10 rounded-full border border-gold/30 object-cover" />
                <span className="font-display text-xs font-bold text-slate-200 sm:text-sm">{f.label}</span>
              </Link>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden flex-col items-center gap-10 lg:flex">
          <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <GoldSeal size={300} />
          </motion.div>
          <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }} className="glass-panel absolute -bottom-6 right-2 rounded-2xl border border-gold/25 p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300"><Check size={16} /></span>
              <div className="text-xs">
                <p className="font-bold text-emerald-300">ویزای بشر دوستانه صادر شد — روز ۵۰ کاری</p>
                <p className="mt-0.5 text-slate-400" dir="ltr">GM7-2026-99</p>
              </div>
            </div>
          </motion.div>
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="glass-panel absolute -top-4 left-0 rounded-2xl border border-gold/25 px-4 py-3">
            <div className="flex items-center gap-2 text-xs">
              <Star size={14} className="fill-gold text-gold" />
              <span className="font-bold text-slate-200">۹۸٪ نرخ موفقیت</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const CountryCard = ({ s, className = "", big = false }) => (
  <Link to={`/countries/${s.slug}`} className={`group relative block overflow-hidden rounded-3xl border border-gold/15 ${className}`} data-testid={`country-card-${s.slug}`}>
    <img src={s.image} alt={s.name} className="img-treatment absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/45 to-transparent" />
    <div className="absolute inset-0 border-2 border-transparent transition-colors duration-500 group-hover:border-gold/40 rounded-3xl" />
    <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
      <span className="mb-3 w-fit rounded-full border border-gold/40 bg-navy-950/70 px-3 py-1 font-display text-[11px] font-black tracking-widest text-gold" dir="ltr">{s.code}</span>
      <h3 className={`font-display font-extrabold text-slate-50 ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{s.name}</h3>
      <p className="mt-1 text-sm text-gold/90">{s.tagline}</p>
      {big && <p className="mt-3 max-w-md text-sm leading-7 text-slate-300 line-clamp-2">{s.desc}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {s.visaTypes.slice(0, big ? 4 : 2).map((v) => (
          <span key={v.title} className="rounded-full bg-navy-900/80 px-3 py-1 text-[11px] text-slate-300 backdrop-blur">{v.title}</span>
        ))}
        <span className="ms-auto flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-navy-950">
          <ArrowLeft size={16} />
        </span>
      </div>
    </div>
  </Link>
);

const CountriesBento = () => {
  const { data, isLoading } = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const services = data?.data ?? [];
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="countries-bento-section">
      <SectionHeading eyebrow="مقاصد مهاجرتی" title="چهار مسیر، <span class='gold-text'>یک تیم حقوقی</span>" desc="بر اساس پروفایل شما، بهترین کشور و بهترین مسیر حقوقی را طراحی می‌کنیم — نه مسیر عمومی، مسیر اختصاصی شما را." />
      {isLoading ? (
        <div className="mt-12 h-96 animate-pulse rounded-3xl bg-navy-850/50" />
      ) : (
        <Stagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
          {services[0] && <StaggerItem key="usa" className="md:col-span-2 lg:col-span-2 lg:row-span-2"><CountryCard s={services[0]} big className="h-full min-h-[440px]" /></StaggerItem>}
          {services[1] && <StaggerItem key="ca" className=""><CountryCard s={services[1]} className="h-full min-h-[260px]" /></StaggerItem>}
          {services[2] && <StaggerItem key="au" className=""><CountryCard s={services[2]} className="h-full min-h-[260px]" /></StaggerItem>}
          {services[3] && <StaggerItem key="sch" className="md:col-span-2 lg:col-span-2"><CountryCard s={services[3]} className="h-full min-h-[260px]" /></StaggerItem>}
        </Stagger>
      )}
    </section>
  );
};

const WHY = [
  { icon: ShieldCheck, title: "تضمین قانونی", desc: "تمام اقدامات زیر نظر وکلای رسمی دادگستری و با قرارداد مکتوب و مهرشده انجام می‌شود؛ بدون واسطه و بدون ریسک." },
  { icon: Eye, title: "شفافیت مالی", desc: "قرارداد به دلار آمریکا؛ تسویه‌حساب کامل بعد از رسیدن به مقصد — نقدی یا اقساطی در ۱۰ ماه." },
  { icon: Radar, title: "رهگیری آنلاین ۲۴/۷", desc: "وضعیت پرونده، به‌روزرسانی‌ها و اقدام بعدی را در هر لحظه از پنل رهگیری آنلاین ببینید." },
  { icon: FileCheck2, title: "بازبینی سه‌لایه مدارک", desc: "هر پرونده پیش از سابمیت، سه بار توسط کارشناس، وکیل ارشد و مدیر پرونده بازبینی می‌شود." },
];

const WhyGM7 = () => (
  <section className="relative overflow-hidden border-y border-gold/10 bg-navy-900/40 py-24" data-testid="why-section">
    <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-gold/5 blur-3xl" />
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
      <div className="flex flex-col items-center gap-8">
        <Reveal><GoldSeal size={250} /></Reveal>
        <Reveal delay={0.15} className="text-center">
          <h3 className="font-display text-2xl font-extrabold">چرا گروه <span className="gold-text">۷</span>؟</h3>
          <p className="mt-3 max-w-xs text-sm leading-7 text-slate-400">هفت اصل طلایی: صداقت، تخصص، شفافیت، پیگیری، سرعت، محرمانگی و نتیجه.</p>
        </Reveal>
      </div>
      <Stagger className="grid gap-5 sm:grid-cols-2">
        {WHY.map((w, i) => (
          <StaggerItem key={w.title}>
            <div className="card-glow group h-full rounded-3xl border border-gold/15 bg-navy-850/70 p-7" data-testid={`why-card-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold"><w.icon size={22} /></span>
                <span className="font-display text-2xl font-black text-gold/20">{toFa(`0${i + 1}`)}</span>
              </div>
              <h4 className="mt-5 font-display text-lg font-bold text-slate-50">{w.title}</h4>
              <p className="mt-2 text-sm leading-7 text-slate-400">{w.desc}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const LawyersSpotlight = () => {
  const { data, isLoading } = useQuery({ queryKey: ["lawyers"], queryFn: fetchLawyers });
  const lawyers = data?.data ?? [];
  const lead = lawyers.find((l) => l.slug === "dr-mohammad-ahmadi") ?? lawyers[0];
  if (isLoading) return <div className="mx-auto h-96 max-w-7xl animate-pulse rounded-3xl bg-navy-850/50" />;
  if (!lead) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="lawyers-section">
      <SectionHeading eyebrow="تیم وکلا و مشاوران" title="مدیریت شخصی <span class='gold-text'>دکتر محمد احمدی</span>" desc="تمام پرونده‌ها شخصاً زیر نظر دکتر احمدی مدیریت می‌شوند؛ نه کارشناس فروش، که خودِ وکیل." />
      <Reveal delay={0.1} className="mt-14">
        <div className="card-glow grid overflow-hidden rounded-3xl border border-gold/25 bg-navy-850 lg:grid-cols-[0.85fr_1.15fr]" data-testid="lead-lawyer-card">
          <div className="relative min-h-80 overflow-hidden">
            <img src={lead.image} alt={lead.name} className="img-treatment absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 28%" }} />
            <div className="absolute inset-0 bg-gradient-to-l from-navy-850/70 to-transparent" />
            <span className="absolute right-5 top-5 rounded-full bg-gold px-4 py-1.5 text-xs font-black text-navy-950">مدیرعامل و بنیان‌گذار</span>
          </div>
          <div className="p-8 sm:p-10">
            <h3 className="font-display text-3xl font-black text-slate-50">{lead.name}</h3>
            <p className="mt-1 text-sm font-bold text-gold">{lead.role} — {toFa(lead.experience)}+ سال سابقه</p>
            <p className="mt-5 text-sm leading-8 text-slate-300">{lead.bio}</p>
            <p className="mt-3 text-sm leading-8 text-slate-400">{lead.bio2}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {lead.licenses?.map((l) => <span key={l} className="rounded-full border border-gold/30 px-3 py-1.5 text-[11px] font-semibold text-gold">{l}</span>)}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {lead.specialties?.map((sp) => <span key={sp} className="rounded-full bg-navy-700 px-3 py-1 text-[11px] text-slate-300">{sp}</span>)}
            </div>
            <Link to="/contact" className="btn-gold mt-8" data-testid="lead-lawyer-consult-cta">رزرو مشاوره با دکتر احمدی</Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const TrackingSection = () => (
  <section className="relative border-y border-gold/10 bg-navy-900/40 py-24" data-testid="home-tracking-section">
    <div className="mx-auto max-w-3xl px-5">
      <SectionHeading eyebrow="شفافیت کامل" title="پرونده‌تان را <span class='gold-text'>لحظه‌به‌لحظه</span> ببینید" desc="با کد رهگیری اختصاصی، مرحله فعلی، تاریخ به‌روزرسانی‌ها و اقدام بعدی پرونده را در لحظه بررسی کنید." />
      <Reveal delay={0.15} className="mt-10">
        <TrackingWidget />
      </Reveal>
    </div>
  </section>
);

const PAY_TERMS = [
  { icon: Wallet, title: "تسویه‌حساب پس از رسیدن", desc: "هیچ مبلغی قبل از نتیجه تسویه نمی‌شود؛ پرداخت اصلی، بعد از رسیدن شما به مقصد انجام می‌گیرد." },
  { icon: FileCheck2, title: "نقدی یا اقساطی", desc: "شرایط نقدی و اقساطی هر دو فراهم است تا راه مهاجرت برای شما آسان‌تر شود." },
  { icon: Clock, title: "۱۰ قسط بدون استرس", desc: "پرداخت در ۱۰ ماه؛ قسط اول، یک ماه بعد از رسیدن شما به مقصد شروع می‌شود." },
  { icon: ShieldCheck, title: "قرارداد به دلار آمریکا", desc: "همه قراردادها برای همه کشورها به دلار آمریکا و با شفافیت کامل تنظیم می‌شود." },
];

const PaymentSection = () => (
  <section className="relative border-y border-gold/10 bg-navy-900/40 py-24" data-testid="payment-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading eyebrow="قرارداد و پرداخت" title="هیچ مبلغی قبل از <span class='gold-text'>رسیدن به مقصد</span> تسویه نمی‌شود" desc="برای آسان کردن راه مهاجرت شما: قرارداد شفاف به دلار آمریکا با شرایط نقدی و اقساطی." />
      <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PAY_TERMS.map((w, i) => (
          <StaggerItem key={w.title}>
            <div className="card-glow group h-full rounded-3xl border border-gold/15 bg-navy-850/70 p-7" data-testid={`payment-card-${i + 1}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold"><w.icon size={22} /></span>
              <h4 className="mt-5 font-display text-lg font-bold text-slate-50">{w.title}</h4>
              <p className="mt-2 text-sm leading-7 text-slate-400">{w.desc}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="mt-10 text-center">
        <Link to="/contact" className="btn-gold" data-testid="payment-consult-cta">درخواست مشاوره رایگان<ArrowLeft size={16} /></Link>
      </Reveal>
    </div>
  </section>
);

const BlogLatest = () => {
  const { data, isLoading } = useQuery({ queryKey: ["posts"], queryFn: fetchPosts });
  const posts = (data?.data ?? []).slice(0, 3);
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="home-blog-section">
      <SectionHeading eyebrow="وبلاگ و اخبار" title="تازه‌ترین <span class='gold-text'>اخبار مهاجرتی</span>" desc="تحلیل‌های حقوقی تیم ما از آخرین تغییرات قوانین ویزا و اقامت چهار مقصد اصلی." />
      {isLoading ? (
        <div className="mt-12 grid gap-6 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-3xl bg-navy-850/50" />)}</div>
      ) : (
        <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
          {posts.map((p) => (
            <StaggerItem key={p.slug}>
              <Link to={`/blog/${p.slug}`} className="card-glow group flex h-full flex-col overflow-hidden rounded-3xl border border-gold/15 bg-navy-850" data-testid={`blog-card-${p.slug}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image} alt={p.title} className="img-treatment h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute right-4 top-4 rounded-full bg-navy-950/85 px-3 py-1 text-[11px] font-bold text-gold backdrop-blur">{p.category}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h4 className="font-display text-base font-bold leading-7 text-slate-50">{p.title}</h4>
                  <p className="mt-3 line-clamp-2 flex-1 text-sm leading-7 text-slate-400">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    <span>{p.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{toFa(p.readMinutes)} دقیقه مطالعه</span>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      )}
      <Reveal className="mt-10 text-center">
        <Link to="/blog" className="btn-ghost" data-testid="blog-all-link">همه مقالات<ArrowLeft size={16} /></Link>
      </Reveal>
    </section>
  );
};

const FaqHome = () => {
  const { data, isLoading } = useQuery({ queryKey: ["faqs"], queryFn: fetchFaqs });
  const faqs = (data?.data ?? []).slice(0, 5);
  return (
    <section className="mx-auto max-w-3xl px-5 py-24" data-testid="home-faq-section">
      <SectionHeading eyebrow="سوالات متداول" title="پاسخ شفاف به <span class='gold-text'>ابهام‌ها</span>" />
      <Reveal delay={0.1} className="mt-12">
        {isLoading ? <div className="h-64 animate-pulse rounded-3xl bg-navy-850/50" /> : <FaqAccordion items={faqs} />}
      </Reveal>
      <Reveal className="mt-8 text-center">
        <Link to="/faq" className="text-sm font-bold text-gold transition-all hover:tracking-wide" data-testid="faq-all-link">مشاهده همه سوالات متداول ←</Link>
      </Reveal>
    </section>
  );
};

const ContactCTA = () => (
  <section className="relative border-t border-gold/10 bg-navy-900/40 py-24" id="consult" data-testid="home-contact-section">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
      <div>
        <SectionHeading align="start" eyebrow="مشاوره تخصصی" title="اولین قدم را <span class='gold-text'>رایگان</span> بردارید" desc="فرم را تکمیل کنید؛ کارشناس ارشد گروه ۷ در کمتر از ۲۴ ساعت کاری با شما تماس می‌گیرد و مسیر دقیق را با زمان و هزینه شفاف اعلام می‌کند." />
        <Reveal delay={0.15} className="mt-8 space-y-4">
          {["جلسه اول کاملاً رایگان و بدون تعهد است", "ارزیابی پرونده توسط خودِ وکیل، نه فروشنده", "پاسخ مکتوب مسیر پیشنهادی با زمان‌بندی دقیق"].map((t) => (
            <p key={t} className="flex items-center gap-3 text-sm text-slate-300"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-gold"><Check size={13} /></span>{t}</p>
          ))}
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <div className="glass-panel rounded-3xl border border-gold/25 p-7 sm:p-9">
          <h3 className="mb-6 font-display text-lg font-extrabold text-gold">فرم درخواست مشاوره رایگان</h3>
          <ConsultationForm />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <StatsBand />
      <CountriesBento />
      <WhyGM7 />
      <LawyersSpotlight />
      <TrackingSection />
      <PaymentSection />
      <TestimonialsSection />
      <BlogLatest />
      <FaqHome />
      <ContactCTA />
    </>
  );
}

function TestimonialsSection() {
  const { data, isLoading } = useQuery({ queryKey: ["testimonials"], queryFn: fetchTestimonials });
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="testimonials-section">
      <SectionHeading eyebrow="داستان‌های موفقیت" title="از رؤیا تا <span class='gold-text'>ویزا</span>؛ به روایت موکلان" />
      <Reveal delay={0.1} className="mt-14">
        {isLoading ? <div className="mx-auto h-72 max-w-4xl animate-pulse rounded-3xl bg-navy-850/50" /> : <Testimonials items={data?.data} />}
      </Reveal>
    </section>
  );
}
